/**
 * Report-action nudge — a once-per-report reminder that the caller has reports awaiting their
 * verification. Surfaced only on `wayai login` and `wayai status` (both already authenticated and
 * infrequent), so there is no per-command polling and thus no rate-limit pressure.
 *
 * Actionable states for a reporter:
 *   - `shipped`                         → accept or contest the fix
 *   - `dismissed` (not final, < cap)    → contest the dismissal
 * (`addressed` is terminal — nothing to do.)
 *
 * Dedup: each `<report_id>:<status>` is shown once, tracked in ~/.wayai/config.json. The shipped
 * EMAIL is the primary, reliable channel; this is a best-effort secondary reminder for CLI users.
 */

import { ApiClient } from './api-client.js';
import { readConfig, writeConfig } from './config.js';

const MAX_TRACKED = 200;

/**
 * Print a one-time nudge for reports awaiting the caller's accept/contest, if any are new.
 * Best-effort and interactive-only: never throws, never prints in non-TTY/CI/JSON contexts
 * (the caller passes a TTY-resolved `accessToken`; this also self-guards on `process.stdout.isTTY`).
 */
export async function nudgeReportActions(apiUrl: string, accessToken: string): Promise<void> {
  if (!process.stdout.isTTY || process.env.CI) return;
  try {
    const client = new ApiClient({ apiUrl, accessToken });
    const [shipped, dismissed] = await Promise.all([
      client.listMyReports('shipped'),
      client.listMyReports('dismissed'),
    ]);

    const actionable = [
      // Shipped re-fires per shipment: a contested fix that's re-shipped is a NEW actionable event
      // (the backend clears notified_at_shipped on `contested`, so the email re-fires too). Key on
      // `contest_count` — it increments once per re-ship cycle (shipped → contested → re-ship) — so
      // the CLI nudge tracks the email channel rather than going silent after the first shipment.
      ...shipped.data.map((r) => ({
        id: r.report_id,
        status: r.status,
        key: `${r.report_id}:shipped:${r.contest_count}`,
        verb: 'accept or contest',
      })),
      // A `final` dismissal can't be contested — don't nudge for it. (`dismissal_final` is the
      // reporter-facing contestability gate; triage sets it precisely when further contest is
      // refused, so we don't need the server-side contest cap here.) Keyed once-ever
      // (no shipment discriminator) to match the dismissed email, whose slot is never re-armed.
      ...dismissed.data
        .filter((r) => !r.dismissal_final)
        .map((r) => ({ id: r.report_id, status: r.status, key: `${r.report_id}:dismissed`, verb: 'contest' })),
    ];
    if (actionable.length === 0) return;

    const config = readConfig();
    if (!config) return;
    const seen = new Set(config.nudged_report_ids ?? []);
    const fresh = actionable.filter((a) => !seen.has(a.key));
    if (fresh.length === 0) return;

    const n = fresh.length;
    console.error(`\n📬 ${n} report${n > 1 ? 's' : ''} awaiting your review:`);
    for (const a of fresh) {
      console.error(`   • ${a.id} (${a.status}) — ${a.verb}`);
    }
    console.error('   → `wayai report list --status shipped`, then `wayai report accept <id>` or `wayai report contest <id> --reason "..."`');

    const updated = [...seen, ...fresh.map((a) => a.key)];
    writeConfig({ ...config, nudged_report_ids: updated.slice(-MAX_TRACKED) });
  } catch {
    /* best-effort: a nudge must never break login/status */
  }
}
